<template>
    <form-wrapper @submit="handleFormSubmission(data)">
        <intake-form-section :title="$t('forms.intake.what_happened.sections.incident_type')">
            <form-row>
                <form-cell>
                    <radio-field
                        :options="incidentTypeOptions"
                        :isRequired="true"
                        :formData="data"
                        valuePath="incidentType"
                        direction="column"
                        @change="change($event)"
                    ></radio-field>
                </form-cell>
            </form-row>
        </intake-form-section>

        <intake-form-section :title="$t('forms.intake.what_happened.sections.date_location')">
            <form-row align="bottom" class="what-happened__date-row">
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.what_happened.fields.incident_date.label')"
                        :isRequired="true"
                        type="date"
                        :formData="data"
                        valuePath="incidentDate"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
                <form-cell width="half">
                    <select-field
                        :label="$t('forms.intake.what_happened.fields.date_certainty.label')"
                        :placeholder="$t('forms.intake.what_happened.fields.date_certainty.placeholder')"
                        :options="dateCertaintyOptions"
                        :formData="data"
                        valuePath="dateCertainty"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.what_happened.fields.city.label')"
                        :placeholder="$t('forms.intake.what_happened.fields.city.placeholder')"
                        :formData="data"
                        valuePath="city"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.what_happened.fields.state.label')"
                        :placeholder="$t('forms.intake.what_happened.fields.state.placeholder')"
                        :formData="data"
                        valuePath="state"
                        :maxLength="2"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <textarea-field
                        :label="$t('forms.intake.what_happened.fields.description.label')"
                        :placeholder="$t('forms.intake.what_happened.fields.description.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        valuePath="description"
                        @change="change($event)"
                    ></textarea-field>
                </form-cell>
            </form-row>
        </intake-form-section>

        <intake-form-section v-if="isMotorVehicleAccident" :title="$t('forms.intake.what_happened.sections.motor_vehicle')">
            <form-row>
                <form-cell>
                    <select-field
                        :label="$t('forms.intake.what_happened.fields.vehicle_role.label')"
                        :placeholder="$t('forms.intake.what_happened.fields.vehicle_role.placeholder')"
                        :options="vehicleRoleOptions"
                        :formData="data"
                        valuePath="vehicleRole"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <tabs-field
                        :label="$t('forms.intake.what_happened.fields.seat_belt.label')"
                        :options="triStateWithNaOptions"
                        :formData="data"
                        valuePath="seatBelt"
                        size="small"
                        @change="change($event)"
                    ></tabs-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <multi-select-field
                        :label="$t('forms.intake.what_happened.fields.vehicle_struck.label')"
                        :placeholder="$t('forms.intake.what_happened.fields.vehicle_struck.placeholder')"
                        :options="vehicleStruckOptions"
                        :formData="data"
                        valuePath="vehicleStruck"
                        @change="change($event)"
                    ></multi-select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <tabs-field
                        :label="$t('forms.intake.what_happened.fields.airbags.label')"
                        :options="triStateOptions"
                        :formData="data"
                        valuePath="airbagsDeployed"
                        size="small"
                        @change="change($event)"
                    ></tabs-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <tabs-field
                        :label="$t('forms.intake.what_happened.fields.head_strike.label')"
                        :options="triStateOptions"
                        :formData="data"
                        valuePath="headStrike"
                        size="small"
                        @change="change($event)"
                    ></tabs-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <tabs-field
                        :label="$t('forms.intake.what_happened.fields.dazed.label')"
                        :options="triStateOptions"
                        :formData="data"
                        valuePath="dazedOrConfused"
                        size="small"
                        @change="change($event)"
                    ></tabs-field>
                </form-cell>
            </form-row>
        </intake-form-section>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('common.microcopy.next')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import IntakeFormSection from 'components/forms/intake/IntakeFormSection'

    export default {
        mixins: [FormMixin],

        components: {
            IntakeFormSection,
        },

        props: {
            initialData: { type: Object, default: undefined },
        },

        data() {
            return {
                data: {
                    incidentType: 'motor_vehicle',
                    incidentDate: undefined,
                    dateCertainty: 'approximate',
                    city: undefined,
                    state: undefined,
                    description: undefined,
                    vehicleRole: undefined,
                    seatBelt: undefined,
                    vehicleStruck: [],
                    airbagsDeployed: undefined,
                    headStrike: undefined,
                    dazedOrConfused: undefined,
                    ...(this.initialData || {}),
                },
            }
        },

        computed: {
            isMotorVehicleAccident() {
                return this.data.incidentType === 'motor_vehicle'
            },

            incidentTypeOptions() {
                return [
                    'motor_vehicle',
                    'construction',
                    'other_workplace',
                    'slip_trip_fall',
                    'pedestrian_bicycle',
                    'assault',
                    'sports',
                    'other',
                ].map((value) => ({
                    label: this.$t(`forms.intake.what_happened.fields.incident_type.options.${value}`),
                    value,
                }))
            },

            dateCertaintyOptions() {
                return ['exact', 'approximate', 'unknown'].map((value) => ({
                    label: this.$t(`forms.intake.what_happened.fields.date_certainty.options.${value}`),
                    value,
                }))
            },

            vehicleRoleOptions() {
                return [
                    'driving',
                    'front_seat_passenger',
                    'rear_seat_passenger',
                    'pedestrian',
                    'cyclist',
                    'motorcycle_rider',
                    'other',
                ].map((value) => ({
                    label: this.$t(`forms.intake.what_happened.fields.vehicle_role.options.${value}`),
                    value,
                }))
            },

            vehicleStruckOptions() {
                return [
                    {
                        heading: null,
                        options: [
                            'rear_impact',
                            'front_impact',
                            'driver_side',
                            'passenger_side',
                            'multiple_impacts',
                            'rollover',
                            'unsure',
                        ].map((value) => ({
                            label: this.$t(`forms.intake.what_happened.fields.vehicle_struck.options.${value}`),
                            value,
                        })),
                    },
                ]
            },

            triStateOptions() {
                return ['yes', 'no', 'unsure'].map((value) => ({
                    label: this.$t(`forms.intake.what_happened.fields.tri_state.options.${value}`),
                    value,
                }))
            },

            triStateWithNaOptions() {
                return ['yes', 'no', 'not_applicable', 'unsure'].map((value) => ({
                    label: this.$t(`forms.intake.what_happened.fields.tri_state.options.${value}`),
                    value,
                }))
            },
        },

        methods: {
            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';

    .what-happened__date-row {
        ::v-deep .form-field {
            justify-content: flex-end;
        }

        ::v-deep .form-field__label {
            min-height: rem(40px);
            display: flex;
            align-items: flex-end;
        }

        ::v-deep .form-field__input {
            height: auto;
            align-items: stretch;
        }

        ::v-deep .form-input-text input[type='date'],
        ::v-deep .form-input-select {
            height: rem($height--input);
        }
    }
</style>
